import { useState } from "react";
import Markdown from "react-markdown";
import "../styles/prose.css";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Rating } from "../constants";

// The FSRS rating controls: one Button style (outline), four clear labels.
// Colour lives only on hover/active — never four differently-coloured
// buttons (task #34 note).
const RATING_BUTTONS = [
  {
    rating: Rating.Again,
    label: "Again",
    hoverClass:
      "hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive active:border-destructive/50 active:bg-destructive/10 active:text-destructive",
  },
  {
    rating: Rating.Hard,
    label: "Hard",
    hoverClass:
      "hover:border-warning/50 hover:bg-warning/10 hover:text-warning active:border-warning/50 active:bg-warning/10 active:text-warning",
  },
  {
    rating: Rating.Good,
    label: "Good",
    hoverClass:
      "hover:border-success/50 hover:bg-success/10 hover:text-success active:border-success/50 active:bg-success/10 active:text-success",
  },
  {
    rating: Rating.Easy,
    label: "Easy",
    hoverClass:
      "hover:border-primary/50 hover:bg-primary/10 hover:text-primary active:border-primary/50 active:bg-primary/10 active:text-primary",
  },
];

const MemoryCard = ({
  memory,
  onMemoryHide,
  onMemoryRate,
  onMemorySkip,
  isProcessing = false,
}) => {
  const [showAnswer, setShowAnswer] = useState(false);

  return (
    <div className="rounded-lg border bg-card p-4 text-card-foreground shadow-xs">
      <h3 className="mb-2 font-display text-lg font-semibold">Question</h3>
      <div className="prose-content">
        <Markdown>{memory.question}</Markdown>
      </div>

      {!showAnswer && (
        <div className="mt-4 space-y-3">
          <Button
            type="button"
            size="lg"
            className="w-full"
            onClick={() => setShowAnswer(true)}
          >
            Show Answer
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-11 text-muted-foreground md:h-8"
              onClick={onMemorySkip}
              disabled={isProcessing}
            >
              Skip
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-11 text-muted-foreground md:h-8"
              onClick={() => onMemoryHide(memory.id)}
              disabled={isProcessing}
            >
              Hide
            </Button>
          </div>
        </div>
      )}

      {showAnswer && (
        <div className="mt-4 border-t pt-4">
          <h3 className="mb-2 font-display text-lg font-semibold">Answer</h3>
          <div className="prose-content">
            <Markdown>{memory.answer}</Markdown>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {RATING_BUTTONS.map(({ rating, label, hoverClass }) => (
              <Button
                key={label}
                type="button"
                variant="outline"
                size="sm"
                className={cn("h-11 w-full md:h-9", hoverClass)}
                onClick={() => onMemoryRate(memory.id, rating)}
                disabled={isProcessing}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

MemoryCard.propTypes = {
  memory: PropTypes.object.isRequired,
  onMemoryHide: PropTypes.func.isRequired,
  onMemoryRate: PropTypes.func.isRequired,
  onMemorySkip: PropTypes.func.isRequired,
  isProcessing: PropTypes.bool,
};

export default MemoryCard;
